import { create } from "zustand";
import { supabase } from "../lib/supabase";
import { Database } from "../lib/database";
import { PhotoFile } from "@/types";
import { v4 as uuidv4 } from "uuid";
import { extractExifData } from "../utils/exifUtils";

type Photo = Database["public"]["Tables"]["photos"]["Row"];

// Helper function to sanitize user input
function sanitizeInput(input: string): string {
  return input.trim().replace(/[<>]/g, "");
}

interface PhotoState {
  photos: Photo[];
  photosLoading: boolean;
  error: string | null;

  fetchPhotos: (listId?: string) => Promise<void>;
  fetchPhotosForPlace: (placeId: string, userId: string) => Promise<Photo[]>;
  uploadPhoto: (
    photo: PhotoFile,
    userId: string,
    listId: string,
    placeId: string,
  ) => Promise<void>;
  deletePhoto: (id: string) => Promise<void>;
  updatePhoto: (id: string, updates: Partial<Photo>) => Promise<void>;
  fetchPhotosForPlaceByOwner: (placeId: string, ownerId: string) => Promise<Photo[]>;

  clearError: () => void;
}

export const usePhotoStore = create<PhotoState>((set) => ({
  photos: [],
  photosLoading: false,
  error: null,

  fetchPhotos: async (listId) => {
    try {
      set({ photosLoading: true, error: null });

      let query = supabase.from("photos").select("*");

      if (listId) {
        query = query.eq("list_id", listId);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;

      set({ photos: data || [] });
    } catch (error) {
      const message = "Failed to fetch photos";
      console.error(message, error);
      set({ error: message });
    } finally {
      set({ photosLoading: false });
    }
  },

  fetchPhotosForPlace: async (placeId, userId) => {
    try {
      const { data, error } = await supabase
        .from("photos")
        .select("*")
        .eq("place_id", placeId)
        .eq("user_id", userId)
        .order("taken_at", { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error fetching photos for place:", error);
      return [];
    }
  },

  uploadPhoto: async (photo, userId, listId, placeId) => {
    set({ photosLoading: true, error: null });
    const uniqueId = uuidv4();
    const ext = photo.file.name.split(".").pop() || "jpg";
    const filePath = `${userId}/${placeId}/${uniqueId}.${ext}`;

    try {
      // Upload file to storage
      const { error } = await supabase.storage
        .from("place-photos")
        .upload(filePath, photo.file, {
          cacheControl: "3600",
          contentType: "image/*",
          upsert: false,
        });
      if (error) {
        throw new Error(`Failed to upload photo: ${photo.name}`);
      }

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("place-photos").getPublicUrl(filePath);

      const thumbnailUrl = publicUrl;

      // Prepare photo data
      const photoData: Database["public"]["Tables"]["photos"]["Insert"] = {
        user_id: userId,
        list_id: listId || null,
        place_id: placeId || null,
        file_name: filePath,
        file_url: publicUrl,
        thumbnail_url: thumbnailUrl,
        file_size: photo.file.size,
        mime_type: photo.file.type,
        tags: [],
        is_favorite: false,
        exif_data: {},
      };

      // Extract location and date from EXIF if available
      const exifData = await extractExifData(photo.file);
      photoData.exif_data = JSON.stringify(exifData);

      // Save photo record to database
      const { error: dbError } = await supabase
        .from("photos")
        .insert(photoData)
        .select()
        .single();

      if (dbError) {
        // Cleanup: Remove uploaded file from storage
        try {
          await supabase.storage.from("place-photos").remove([filePath]);
        } catch {
          // Cleanup errors are intentionally ignored - the main error is more important
        }
        throw new Error(`Failed to save photo to database: ${dbError.message}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to upload photo";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ photosLoading: false });
    }
  },

  deletePhoto: async (id) => {
    try {
      set({ photosLoading: true, error: null });

      // Get photo data to delete file from storage
      const { data: photo } = await supabase
        .from("photos")
        .select("file_url")
        .eq("id", id)
        .single();

      if (photo?.file_url) {
        // Extract file path from URL
        const url = new URL(photo.file_url);
        const filePath = url.pathname.split(
          "/storage/v1/object/public/place-photos/",
        )[1];

        if (filePath) {
          // Delete file from storage
          await supabase.storage.from("place-photos").remove([filePath]);
        }
      }

      // Delete photo record
      const { error } = await supabase.from("photos").delete().eq("id", id);

      if (error) throw error;

      set((state) => ({
        photos: state.photos.filter((photo) => photo.id !== id),
      }));
    } catch (error) {
      const message = "Failed to delete photo";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ photosLoading: false });
    }
  },

  updatePhoto: async (id, updates) => {
    try {
      set({ photosLoading: true, error: null });

      // Sanitize any text inputs in updates
      const sanitizedUpdates = { ...updates };
      if (sanitizedUpdates.file_name) {
        sanitizedUpdates.file_name = sanitizeInput(sanitizedUpdates.file_name);
      }

      const { data, error } = await supabase
        .from("photos")
        .update({ ...sanitizedUpdates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        photos: state.photos.map((photo) => (photo.id === id ? data : photo)),
      }));
    } catch (error) {
      const message = "Failed to update photo";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ photosLoading: false });
    }
  },

  fetchPhotosForPlaceByOwner: async (placeId, ownerId) => {
    try {
      const { data, error } = await supabase
        .from("photos")
        .select("*")
        .eq("place_id", placeId)
        .eq("user_id", ownerId)
        .order("taken_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching photos for place by owner:", error);
      return [];
    }
  },

  clearError: () => set({ error: null }),
}));
